import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Close } from "@mui/icons-material";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import {
  useDeleteProductMutation,
  useGetAllItemsQuery,
  useGetAllUsersQuery,
  usePartnersQuery,
} from "../../Slices/roomNateApis";
import { useState } from "react";
import { useGlobalAuthContext } from "../../context/AuthContext";
import GroupedGrid from "../GlobalStyles/GroupedGrid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddProductModal from "./AddProductModel";

function formatUsd(price) {
  if (price == null || price === "") return "—";
  const n = Number(price);
  if (Number.isFinite(n)) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
  }
  return `$${price}`;
}

function normalizeUrl(href) {
  if (!href || typeof href !== "string") return "";
  const t = href.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function productItemIds(row) {
  const raw = row?.items;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((it) => (typeof it === "string" ? it : it?._id))
    .filter(Boolean);
}

export default function ProductsTable({
  gridView,
  productsData,
  endDate,
  setEndDate,
  startDate,
  setStartDate,
  user,
  setUser,
}) {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const { data: usersData } = useGetAllUsersQuery(null, {
    skip: !currentUserSessionStorage,
  });

  const { data: partnersData } = usePartnersQuery("", {
    skip: !currentUserSessionStorage,
  });

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = React.useState(null);

  const {
    data: FetchItems,
    refetch,
    isFetching,
  } = useGetAllItemsQuery({ itemType: 1 });

  const itemById = React.useMemo(() => {
    const m = new Map();
    for (const it of FetchItems?.items ?? []) {
      if (it?._id) m.set(it._id, it);
    }
    return m;
  }, [FetchItems]);

  const [deleteProduct, resultStats] = useDeleteProductMutation();
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const [editProduct, setEditProduct] = useState({});
  const [selectedZone] = useState(null);
  const [selectedShape] = useState(null);

  const filteredData = FetchItems?.items?.filter((item) => {
    if (!selectedZone && !selectedShape) {
      return true;
    }
    if (selectedZone) {
      if (
        item.zones
          .map((zone) => zone.toLowerCase())
          .includes(selectedZone.toLowerCase())
      ) {
        return true;
      }
    }
    if (selectedShape) {
      if (
        item.shapes
          .map((shape) => shape.toLowerCase())
          .includes(selectedShape.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  });

  const groupedData = filteredData?.reduce((acc, item) => {
    item.zones.forEach((zone) => {
      if (!acc[zone]) {
        acc[zone] = [];
      }
      acc[zone].push(item);
    });
    return acc;
  }, {});

  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats]);

  const handleDeleteConfirm = () => {
    if (selectedDeleteId) deleteProduct(selectedDeleteId);
    setDeleteOpen(false);
    setSelectedDeleteId(null);
  };

  React.useEffect(() => {
    refetch();
  }, [currentUserSessionStorage, refetch]);

  const openDelete = (id) => {
    setSelectedDeleteId(id);
    setDeleteOpen(true);
  };

  return (
    <>
      {successAlert && (
        <SuccessAlert title="SKU has been deleted successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      {gridView ? (
        <Box>
          {isFetching ? (
            <Typography>Loading...</Typography>
          ) : (
            <GroupedGrid groupedData={groupedData} />
          )}
        </Box>
      ) : (
        <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="h2" fontWeight={700}>
              SKUs
            </Typography>
            <AddProductModal editProduct={editProduct} setEditProduct={setEditProduct} />
          </Stack>

          <Box
            sx={{
              mb: 3,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Search filters
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    disablePortal
                    id="affiliate-user-filter"
                    value={user}
                    options={
                      usersData?.map((u) => ({
                        label: u?.name || "",
                        value: u?._id || "",
                      })) || []
                    }
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    fullWidth
                    onChange={(_, value) => setUser(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Select user" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ ".MuiFormControl-root": { width: "100%" } }}>
                    <DatePicker
                      label="Start date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{ actionBar: { actions: ["clear"] }, textField: { size: "small" } }}
                      sx={{ width: "100%" }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ ".MuiFormControl-root": { width: "100%" } }}>
                    <DatePicker
                      label="End date"
                      minDate={startDate}
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ actionBar: { actions: ["clear"] }, textField: { size: "small" } }}
                      sx={{ width: "100%" }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          {isFetching ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : !productsData?.length ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              No records.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {productsData.map((row, index) => {
                const partnerIds = Array.isArray(row?.partners) ? row.partners : [];
                const partnerRows = partnersData?.filter((p) => partnerIds.includes(p._id)) ?? [];
                const ids = productItemIds(row);
                const colors = Array.isArray(row?.colors) ? row.colors : [];
                const href = row?.url ? normalizeUrl(row.url) : "";

                return (
                  <Grid item xs={12} sm={6} lg={4} key={row._id || index}>
                    <Card
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "box-shadow 0.15s, border-color 0.15s",
                        "&:hover": {
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          borderColor: "text.disabled",
                        },
                      }}
                    >
                      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", pb: 1 }}>
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 140,
                            maxHeight: 180,
                            mb: 1.5,
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {row?.image ? (
                            <Box
                              component="img"
                              src={row.image}
                              alt={row?.name || "Product"}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: 160,
                                width: "auto",
                                height: "auto",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              No image
                            </Typography>
                          )}
                        </Box>

                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, lineHeight: 1.3 }}>
                          {row?.name || "Untitled"}
                        </Typography>

                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                          {formatUsd(row?.price)}
                        </Typography>

                        {href ? (
                          <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              wordBreak: "break-all",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mb: 1.5,
                            }}
                          >
                            {row.url}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5 }}>
                            No destination URL
                          </Typography>
                        )}

                        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.5 }}>
                          Partners
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
                          {partnerRows.length > 0 ? (
                            partnerRows.map((p) => (
                              <Chip key={p._id} label={p.title} size="small" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </Stack>

                        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.5 }}>
                          Items
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
                          {ids.length > 0 ? (
                            ids.map((id) => {
                              const name = itemById.get(id)?.name || id;
                              return <Chip key={id} label={name} size="small" variant="outlined" />;
                            })
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </Stack>

                        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.5 }}>
                          Colors
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
                          {colors.length > 0 ? (
                            colors.map((c, i) => (
                              <Tooltip key={`${c}-${i}`} title={c}>
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 1,
                                    bgcolor: c,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    flexShrink: 0,
                                  }}
                                />
                              </Tooltip>
                            ))
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </Stack>

                        <Chip
                          label={`${row?.clicks ?? 0} click${(row?.clicks ?? 0) !== 1 ? "s" : ""}`}
                          size="small"
                          sx={{ alignSelf: "flex-start", mt: "auto" }}
                        />
                      </CardContent>

                      <Divider />

                      <CardActions sx={{ px: 1.5, py: 1, justifyContent: "flex-end", gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditProduct(row)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => openDelete(row._id)} color="primary">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          <Dialog
            maxWidth="sm"
            fullWidth
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            aria-labelledby="product-delete-title"
            aria-describedby="product-delete-desc"
          >
            <DialogTitle id="product-delete-title">Confirm the action?</DialogTitle>
            <Box position="absolute" top={10} right={0}>
              <IconButton onClick={() => setDeleteOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <DialogContent>
              <DialogContentText id="product-delete-desc">
                Do you really want to delete this product?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
}
